import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext'; // Assumes you store user here
import AdminDashboard from './AdminDashboard';
import { firstVisiblePage } from '../../layouts/Menu.jsx';

const Home = () => {
  const { changeBackground, changeNavigationHader, changeSideBarStyle, chnageSidebarColor } =
    useContext(ThemeContext);

  const { user, hasPermission } = useAuth(); // ✅ assumes user is in AuthContext
  const navigate = useNavigate();
  const [noAccess, setNoAccess] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);

  const profileText = (profile) => {
    if (!profile) return '';
    if (typeof profile === 'string') return profile.toLowerCase();
    if (typeof profile === 'number') return String(profile);
    return String(profile.label || profile.name || profile.value || profile.role || '').toLowerCase();
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('lms_ui_theme');
    if (!savedTheme) {
      changeBackground({ value: 'light', label: 'Light' });
    }
    // Removed hardcoded changeSideBarStyle to allow responsive layout
    changeNavigationHader('color_3');
    chnageSidebarColor('color_1');
  }, [changeBackground, changeNavigationHader, changeSideBarStyle, chnageSidebarColor]);

  useEffect(() => {
    // Check if user exists
    if (!user) {
      setNoAccess(true);
      setShowAdminDashboard(false);
      return;
    }

    const storedRole = String(localStorage.getItem('userRole') || '').toLowerCase();
    const role = String(user.role || storedRole || '').toLowerCase();
    if (role === 'admin') {
      setShowAdminDashboard(true);
      setNoAccess(false);
      return;
    }
    if (role === 'trainer') {
      setShowAdminDashboard(false);
      setNoAccess(false);
      navigate('/trainer-dashboard');
      return;
    }
    if (role === 'student') {
      setShowAdminDashboard(false);
      setNoAccess(false);
      navigate('/student-dashboard');
      return;
    }

    /* No dashboard for this role. That is not the same as no access: a role
       granted, say, Batches and Attendance lands here after signing in and
       used to be told "Access Denied" with both pages a click away. Send
       them to the first page their sidebar shows; only someone with none
       is denied. */
    const denyOrRedirect = () => {
      setShowAdminDashboard(false);
      const isStudentUser = Boolean(user.profiles?.some((p) => (p.label || p.name) === 'Student'));
      const target = firstVisiblePage(hasPermission, isStudentUser);
      if (target) {
        setNoAccess(false);
        navigate(target, { replace: true });
      } else {
        setNoAccess(true);
      }
    };

    // Check for admin/superadmin/CRM first
    const profileIdsText = String(user.profileIds || '').toLowerCase();
    const hasProfile = (value) => user.profiles?.some((p) => profileText(p).includes(value));
    if (user.isSuperAdmin === true || 
        profileIdsText === 'superadmin' ||
        profileIdsText.includes('admin') ||
        hasProfile('superadmin') ||
        hasProfile('admin') ||
        hasProfile('crm')) {
      setShowAdminDashboard(true);
      setNoAccess(false);
      return;
    }

    // If user has profiles array, check for trainer/student
    if (user.profiles?.length) {
      const foundTrainer = user.profiles.find((profile) => profileText(profile).includes('trainer'));
      const foundStudent = user.profiles.find((profile) => profileText(profile).includes('student'));

      if (foundTrainer) {
        setShowAdminDashboard(false);
        setNoAccess(false);
        navigate('/trainer-dashboard');
      } else if (foundStudent) {
        setShowAdminDashboard(false);
        setNoAccess(false);
        navigate('/student-dashboard');
      } else {
        denyOrRedirect();
      }
    } else if (user.profileIds) {
      // Handle profileIds as string for trainer/student
      const profileId = profileIdsText;
      
      if (profileId.includes('trainer')) {
        setShowAdminDashboard(false);
        setNoAccess(false);
        navigate('/trainer-dashboard');
      } else if (profileId.includes('student')) {
        setShowAdminDashboard(false);
        setNoAccess(false);
        navigate('/student-dashboard');
      } else {
        denyOrRedirect();
      }
    } else {
      denyOrRedirect();
    }
  }, [user, navigate, hasPermission]);

  return (
    <>
      {showAdminDashboard && (
        <AdminDashboard user={user} />
      )}
      {noAccess && (
        <div>
          <div>
            <h3>Access Denied</h3>
            <p>You don't have permission to access this area.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
